class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.isProcessingGift = false; // Flag to prevent concurrent gift operations
    this.isGiftOperation = false; // Flag to indicate gift operation in progress
    
    // Listen for quantity changes and remove button clicks to trigger gift logic
    this.addEventListener('change', (event) => {
      console.log('🎯 Change event detected:', {
        target: event.target.tagName,
        name: event.target.name,
        type: event.target.type,
        value: event.target.value,
        matches: event.target.matches('input[name*="quantity"]')
      });
      
      if (event.target.matches('input[name*="quantity"]') || 
          event.target.matches('input[type="number"]') ||
          event.target.matches('.quantity__input')) {
        console.log('🔄 Quantity input changed, triggering gift check');
        setTimeout(() => {
          if (!this.giftOperationInProgress) {
            this.queueGiftOperation(() => this.checkGiftWithPurchase({
              quantity: parseInt(event.target.value),
              id: event.target.dataset.variantId
            })).catch(console.error);
          }
        }, 300);
      }
    });
    
    // Listen for remove button clicks
    this.addEventListener('click', (event) => {
      if (event.target.matches('.cart-remove-button') || 
          event.target.matches('[name="remove"]') ||
          event.target.closest('.cart-remove-button')) {
        console.log('🗑️ Remove button clicked, triggering gift check');
        setTimeout(() => {
          if (!this.giftOperationInProgress) {
            this.queueGiftOperation(() => this.checkGiftWithPurchase({
              action: 'remove',
              id: event.target.dataset.variantId
            })).catch(console.error);
          }
        }, 500); // Reduced delay for removal
      }
    });
    this.lastNonGiftTotal = 0; // Track the last non-gift total
    this.giftAdded = false; // Track if gift is in cart
    this.giftCheckTimeout = null; // Timeout for debouncing gift checks
    this.lastGiftCheckTime = 0; // Track last gift check time
    this.giftOperationInProgress = false; // Global lock for gift operations
    this.giftNumericId = null; // Numeric ID of the gift product
    this.giftIdPromise = null; // Promise for fetching gift ID
    this.operationQueue = []; // Queue for serializing gift operations

    // Pre-fetch gift product ID if feature is active
    const giftActive = window.themeSettings?.gift_with_purchase_active || false;
    const giftProductHandle = window.themeSettings?.gift_with_purchase_product || null;
    if (giftActive && giftProductHandle) {
      this.giftIdPromise = fetch(`/products/${giftProductHandle}.js`)
        .then(response => response.json())
        .then(product => {
          this.giftNumericId = product.id.toString();
          console.log('Gift numeric ID loaded:', this.giftNumericId);
          return this.giftNumericId;
        })
        .catch(error => {
          console.error('Failed to load gift product numeric ID:', error);
          return null;
        });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    console.log('🚨 CART DRAWER renderContents called!', {
      productId: this.productId, 
      operationInProgress: this.giftOperationInProgress, 
      parsedState: parsedState,
      timestamp: new Date().toISOString()
    });
    console.log('Gift check will run:', !this.isProcessingGift && !this.isGiftOperation && !this.giftOperationInProgress);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Queue gift check if no operation is in progress OR if this is a cart removal/change
    const isCartChange = parsedState && (parsedState.id || parsedState.line !== undefined);
    
    if (!this.isProcessingGift && !this.isGiftOperation && (!this.giftOperationInProgress || isCartChange)) {
      clearTimeout(this.giftCheckTimeout);
      this.giftCheckTimeout = setTimeout(() => {
        this.queueGiftOperation(() => this.checkGiftWithPurchase(parsedState)).catch(console.error);
      }, 500);
    } else {
      console.log('Skipping gift check:', {
        hasProductId: !!this.productId,
        isProcessingGift: this.isProcessingGift,
        isGiftOperation: this.isGiftOperation,
        giftOperationInProgress: this.giftOperationInProgress,
        isCartChange
      });
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  async queueGiftOperation(operation) {
    console.log('queueGiftOperation called, current giftOperationInProgress:', this.giftOperationInProgress);
    this.operationQueue.push(operation);
    if (!this.giftOperationInProgress) {
      console.log('Starting processQueue');
      await this.processQueue();
    } else {
      console.log('Skipping processQueue - already in progress');
    }
  }

  async processQueue() {
    console.log('processQueue called, queue length:', this.operationQueue.length);
    if (this.operationQueue.length === 0) {
      console.log('Queue empty, setting giftOperationInProgress to false');
      this.giftOperationInProgress = false;
      return;
    }
    console.log('Setting giftOperationInProgress to true');
    this.giftOperationInProgress = true;
    const operation = this.operationQueue.shift();
    try {
      await operation();
    } catch (error) {
      console.error('Error processing queued operation:', error);
    }
    await this.processQueue();
  }

  async checkGiftWithPurchase(parsedState) {
    console.log('checkGiftWithPurchase called with flags:', {
      isProcessingGift: this.isProcessingGift,
      isGiftOperation: this.isGiftOperation,
      giftOperationInProgress: this.giftOperationInProgress
    });

    if (this.isProcessingGift || this.isGiftOperation) {
      console.log('Gift operation already in progress, skipping');
      return;
    }

    // Prevent too frequent gift checks (minimum 1 second between checks)
    const now = Date.now();
    if (now - this.lastGiftCheckTime < 1000) {
      console.log('Gift check too recent, skipping');
      return;
    }
    this.lastGiftCheckTime = now;

        // Get theme settings
    console.log('🔍 window.themeSettings object:', window.themeSettings);
    console.log('🔍 window.themeSettings keys:', Object.keys(window.themeSettings || {}));
    console.log('🔍 Direct access test:', {
      direct_gift_promotion_type: window.themeSettings?.gift_promotion_type,
      direct_gift_trigger_product: window.themeSettings?.gift_trigger_product,
      direct_gift_with_purchase_active: window.themeSettings?.gift_with_purchase_active
    });
    
    const giftActive = window.themeSettings?.gift_with_purchase_active || false;
    const giftPromotionType = window.themeSettings?.gift_promotion_type || 'threshold';
    const giftProductHandle = window.themeSettings?.gift_with_purchase_product || null;
    const giftThreshold = window.themeSettings?.gift_with_purchase_threshold || 50;
    const giftTriggerProduct = window.themeSettings?.gift_trigger_product || null;
    
    console.log('🔍 Raw theme settings:', window.themeSettings);
    console.log('Gift settings:', { 
      giftActive, 
      giftPromotionType, 
      giftPromotionTypeType: typeof giftPromotionType,
      giftProductHandle, 
      giftThreshold, 
      giftTriggerProduct 
    });
    
    if (!giftActive || !giftProductHandle) {
      console.log('Gift with purchase not active or no product selected');
      this.giftOperationInProgress = false;
      return;
    }

    // Ensure gift numeric ID is loaded
    if (!this.giftNumericId) {
      this.giftNumericId = await this.giftIdPromise;
      if (!this.giftNumericId) {
        console.log('Failed to load gift numeric ID, skipping');
        this.giftOperationInProgress = false;
        return;
      }
    }

    // Fetch current cart state
    try {
      // Add delay to ensure cart is updated
      await new Promise(resolve => setTimeout(resolve, 500));
      const timestamp = Date.now();
      const cartResponse = await fetch(`/cart.js?t=${timestamp}`);
      const cartState = await cartResponse.json();
      console.log('Current cart state:', cartState);
      console.log('ParsedState:', parsedState);
      console.log('Cart items:', cartState.items.map(item => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        final_price: item.final_price,
        properties: item.properties
      })));

      // Handle empty cart case
      if (!cartState.items || cartState.items.length === 0) {
        console.log('Cart is empty');
        if (this.giftAdded) {
          this.giftAdded = false;
          this.lastNonGiftTotal = 0;
        }
        this.giftOperationInProgress = false;
        return;
      }

             // Calculate non-gift total using final_price to account for discounts
       console.log('🔍 Gift detection debug:', {
         giftNumericId: this.giftNumericId,
         cartItems: cartState.items.map(item => ({
           id: item.id, 
           product_id: item.product_id, 
           handle: item.handle,
           title: item.title,
           quantity: item.quantity,
           properties: item.properties
         }))
       });
       
       // Only treat items with _is_gift property as gift items
       const giftItems = cartState.items.filter(item => 
         item.product_id.toString() === this.giftNumericId && 
         item.properties && 
         item.properties._is_gift === 'true'
       );
       // Non-gift items = all items that aren't gifts (including manually added gift product)
       const nonGiftItems = cartState.items.filter(item => 
         item.product_id.toString() !== this.giftNumericId ||
         !item.properties ||
         item.properties._is_gift !== 'true'
       );
      const nonGiftTotal = nonGiftItems.reduce((sum, item) => sum + (item.final_price * item.quantity), 0) / 100;
      const cartTotal = nonGiftTotal; // Use non-gift total for threshold comparison

      console.log('Non-gift total:', nonGiftTotal, 'Gift items count:', giftItems.length);

      // Handle quantity changes
      if (parsedState && parsedState.quantity !== undefined && nonGiftItems.length === 1) {
        const actualQuantity = nonGiftItems[0].quantity;
        const expectedTotal = actualQuantity * (nonGiftItems[0].final_price / 100);
        console.log('Quantity change detected - parsedState:', parsedState.quantity, 'actual cart quantity:', actualQuantity);
        console.log('Cart total from API:', cartTotal, 'Expected total based on actual quantity:', expectedTotal);
        // Use expected total for consistency
        this.nonGiftTotal = expectedTotal;
      } else {
        this.nonGiftTotal = nonGiftTotal;
      }

      console.log('Cart analysis:', { 
        cartTotal, 
        nonGiftTotal: this.nonGiftTotal, 
        giftItems: giftItems.length, 
        threshold: giftThreshold,
        cartStateItems: cartState.items,
        cartStateTotalPrice: cartState.total_price
      });

            const currentGiftCount = giftItems.length;
      
      // Check promotion conditions based on type
      let shouldHaveGift = false;
      let promotionReason = '';
      
      console.log('🔍 Promotion type check:', {
        giftPromotionType,
        giftPromotionTypeType: typeof giftPromotionType,
        giftThreshold,
        giftTriggerProduct,
        isThresholdMode: giftPromotionType === 'threshold',
        isBuyXGetYMode: giftPromotionType === 'buy_x_get_y'
      });
      
      if (giftPromotionType === 'threshold') {
        // Threshold-based promotion
        shouldHaveGift = this.nonGiftTotal >= giftThreshold;
        promotionReason = shouldHaveGift ? `Threshold met ($${this.nonGiftTotal} >= $${giftThreshold})` : `Threshold not met ($${this.nonGiftTotal} < $${giftThreshold})`;
        console.log('🔍 Using THRESHOLD logic:', { shouldHaveGift, promotionReason });
      } else if (giftPromotionType === 'buy_x_get_y' && giftTriggerProduct) {
        // Buy X Get Y promotion - check if trigger product is in cart
        console.log('🔍 Buy X Get Y check:', {
          giftTriggerProduct,
          triggerProductType: typeof giftTriggerProduct,
          nonGiftItems: nonGiftItems.map(item => ({
            id: item.id,
            product_id: item.product_id,
            handle: item.handle,
            title: item.title
          }))
        });
        
        // For Buy X Get Y, we need to compare product handles, not IDs
        const triggerProductInCart = nonGiftItems.some(item => item.handle === giftTriggerProduct);
        shouldHaveGift = triggerProductInCart;
        promotionReason = shouldHaveGift ? 'Trigger product found in cart' : 'Trigger product not in cart';
        
        console.log('🔍 Trigger product check result:', {
          triggerProductInCart,
          promotionReason,
          matchingItem: nonGiftItems.find(item => item.handle === giftTriggerProduct)
        });
      }
      
      console.log('State check:', {
        currentGiftCount,
        shouldHaveGift,
        promotionReason,
        promotionType: giftPromotionType,
        lastNonGiftTotal: this.lastNonGiftTotal,
        giftAdded: this.giftAdded,
        threshold: giftThreshold
      });
      
      // Always sync giftAdded with actual cart state
      this.giftAdded = currentGiftCount > 0;
      
      // Proceed with gift state changes based on actual cart state
      if (shouldHaveGift && currentGiftCount === 0) {
        console.log(`Adding gift to cart - ${promotionReason}`);
        this.isProcessingGift = true;
        this.isGiftOperation = true;
        await this.addGiftToCart(giftProductHandle);
      } else if (!shouldHaveGift && currentGiftCount > 0) {
        console.log(`Removing gift from cart - ${promotionReason}`);
        this.isProcessingGift = true;
        this.isGiftOperation = true;
        await this.removeGiftFromCart(giftItems[0].key);
      } else if (shouldHaveGift && currentGiftCount > 1) {
        console.log('Multiple gift items found, removing extras');
        this.isProcessingGift = true;
        this.isGiftOperation = true;
        for (let i = 1; i < giftItems.length; i++) {
          await this.removeGiftFromCart(giftItems[i].key);
        }
      } else {
        console.log('No gift action needed - state unchanged');
        this.giftOperationInProgress = false;
      }

      this.lastNonGiftTotal = this.nonGiftTotal;
    } catch (error) {
      console.error('Error fetching cart state:', error);
      this.giftOperationInProgress = false;
    }
  }

  async addGiftToCart(productHandle) {
    console.log('Adding gift product:', productHandle);
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const product = await response.json();
      const variantId = product.variants[0]?.id;
      console.log('Product variant ID:', variantId);

      if (!variantId) {
        console.error('No variant found for gift product');
        this.isProcessingGift = false;
        this.isGiftOperation = false;
        this.giftOperationInProgress = false;
        this.giftAdded = false;
        return;
      }

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          '_is_gift': 'true'
        },
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const addResponse = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
      const state = await addResponse.text();
      const parsedState = JSON.parse(state);

            console.log('Gift add response:', parsedState);
      
      // Check if gift is actually in cart after adding
      const cartCheckResponse = await fetch('/cart.js');
      const cartCheck = await cartCheckResponse.json();
      console.log('🔍 Cart state after gift addition:', {
        totalItems: cartCheck.items.length,
        items: cartCheck.items.map(item => ({
          title: item.title,
          product_id: item.product_id,
          quantity: item.quantity
        }))
      });
      
      // Check if gift is actually present in the cart (with _is_gift property)
      const giftInCart = cartCheck.items.some(item => 
        item.product_id.toString() === this.giftNumericId &&
        item.properties &&
        item.properties._is_gift === 'true'
      );
      console.log('🎁 Gift detection after addition:', {
        giftInCart,
        expectedId: this.giftNumericId,
        foundItems: cartCheck.items.map(item => ({ 
          id: item.product_id, 
          title: item.title,
          properties: item.properties 
        }))
      });
      
      this.giftAdded = giftInCart;
      this.isProcessingGift = false;
      this.isGiftOperation = false;
      this.giftOperationInProgress = false;
      console.log('Gift addition completed, giftAdded:', this.giftAdded);
      console.log('Flags reset to:', {
        isProcessingGift: this.isProcessingGift,
        isGiftOperation: this.isGiftOperation,
        giftOperationInProgress: this.giftOperationInProgress
      });

      if (giftInCart) {
        console.log('Gift added successfully');
        // Just trigger a simple cart update event, let the theme handle the rendering
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-addition', cartData: cartCheck });
      } else {
        console.error('Gift product not found in response');
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error);
      this.isProcessingGift = false;
      this.isGiftOperation = false;
      this.giftOperationInProgress = false;
      this.giftAdded = false;
      console.log('Gift addition failed, giftAdded reset to:', this.giftAdded);
    }
  }

  async removeGiftFromCart(itemKey) {
    try {
      const body = JSON.stringify({
        id: itemKey,
        quantity: 0,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
      const state = await response.text();
      const parsedState = JSON.parse(state);

      this.isProcessingGift = false;
      this.isGiftOperation = false;
      this.giftOperationInProgress = false;
      this.giftAdded = false;
      console.log('Gift removal completed, giftAdded:', this.giftAdded);
      
      // Just trigger a simple cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-removal' });
    } catch (error) {
      console.error('Error removing gift from cart:', error);
      this.isProcessingGift = false;
      this.isGiftOperation = false;
      this.giftOperationInProgress = false;
      this.giftAdded = false;
      console.log('Gift removal failed, giftAdded reset to:', this.giftAdded);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);